import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useImageUrl } from '@/hooks/useImageUrl';

interface AdminAvatarImageProps {
  src: string | null | undefined;
  fallback: string;
  className?: string;
  fallbackClassName?: string;
}

/**
 * AdminAvatarImage Component
 * 
 * Wrapper for Avatar that uses useImageUrl to generate signed URLs
 * for private profile photos in Admin sections.
 * 
 * Usage:
 * ```tsx
 * <AdminAvatarImage 
 *   src={user.main_photo_url} 
 *   fallback={user.username.charAt(0).toUpperCase()} 
 * />
 * ```
 */
const AdminAvatarImage: React.FC<AdminAvatarImageProps> = ({
  src,
  fallback,
  className = 'h-10 w-10',
  fallbackClassName = 'bg-luxury-gold/20 text-luxury-gold'
}) => {
  const { url: signedUrl, isLoading } = useImageUrl(src, 'avatar');

  return (
    <Avatar className={className}>
      {!isLoading && signedUrl && (
        <AvatarImage src={signedUrl} alt={fallback} />
      )}
      <AvatarFallback className={fallbackClassName}>
        {fallback}
      </AvatarFallback>
    </Avatar>
  );
};

export default AdminAvatarImage;
